/**
 * NAVI floating chat bubble — persistent, non-intrusive assistant available
 * from every wallet screen. Opens a small panel with a threaded
 * conversation. Backed by /api/navi/ask.
 *
 * Language + voice:
 *  - Every NAVI reply (and the opening greeting) is translated into the
 *    consumer's wallet language via /api/wallet/translate.
 *  - NAVI reads her replies aloud using the browser's Web Speech API,
 *    voice-matched to that language. Consumers can mute all speech from
 *    the panel header, or replay any single reply with the speaker
 *    button on that bubble.
 *
 * NAVI never gives financial advice; she explains and clarifies.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { X, Send, Loader2, Volume2, VolumeX, Square } from "lucide-react";
import { NAVY, GOLD } from "@/components/wallet/WalletShell";
import { emitSignal } from "@/lib/wallet-signal";
import { useAccessibility } from "@/contexts/AccessibilityContext";
import NaviFace from "@/components/wallet/NaviFace";
import { useNaviSpeech } from "@/lib/navi-speech";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const GREETING_EN = "Hi — I'm NAVI. I explain financial things in plain language. I never give advice. What would you like me to explain?";

let msgSeq = 0;
const nextId = () => `m_${Date.now()}_${++msgSeq}`;

async function translateIfNeeded(text, language) {
  if (!text || !language || language === "en") return text;
  try {
    const { data } = await axios.post(`${API}/wallet/translate`, { text, target_language: language });
    return data.translated || text;
  } catch { return text; }
}

export default function NaviBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    { id: nextId(), role: "navi", text: GREETING_EN, spoken_text: GREETING_EN },
  ]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [greetingSpokenFor, setGreetingSpokenFor] = useState(null); // language code
  const endRef = useRef(null);
  const { prefs } = useAccessibility();
  const speech = useNaviSpeech(prefs.language || "en");

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  // Listen for external "navi.open" events (fired by Ask-NAVI pills across the wallet)
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const q = e?.detail?.question;
      if (q) setText(q);
    };
    window.addEventListener("navi.open", handler);
    return () => window.removeEventListener("navi.open", handler);
  }, []);

  // Translate the greeting whenever the wallet language changes so that
  // when the panel opens NAVI immediately reads it in that language.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const translated = await translateIfNeeded(GREETING_EN, prefs.language);
      if (cancelled) return;
      setMessages(ms => {
        if (ms.length === 0) return ms;
        const [first, ...rest] = ms;
        if (first.role !== "navi") return ms;
        // Only replace if it's still the untouched greeting
        const isStillGreeting = first.text === GREETING_EN || first.spoken_text === GREETING_EN;
        if (!isStillGreeting) return ms;
        return [{ ...first, text: translated, spoken_text: translated }, ...rest];
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.language]);

  // Speak the greeting the first time the panel opens in a given language
  useEffect(() => {
    if (!open) return;
    const lang = prefs.language || "en";
    if (greetingSpokenFor === lang) return;
    if (!speech.supported || speech.muted) { setGreetingSpokenFor(lang); return; }
    const greeting = messages[0];
    if (greeting?.role !== "navi") return;
    // Wait a tick for voices list to populate on first load
    const t = setTimeout(() => {
      speech.speak(greeting.spoken_text || greeting.text, greeting.id);
      setGreetingSpokenFor(lang);
    }, speech.voicesReady ? 60 : 320);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefs.language, speech.voicesReady, speech.muted]);

  const ask = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true);
    setMessages(m => [...m, { id: nextId(), role: "user", text: q }]);
    setText("");
    emitSignal("navi.question.asked.v1", { question_length: q.length, language: prefs.language });
    try {
      const { data } = await axios.post(`${API}/navi/ask`, { question: q });
      const englishAnswer = data.answer;
      const localAnswer = await translateIfNeeded(englishAnswer, prefs.language);
      const id = nextId();
      setMessages(m => [...m, { id, role: "navi", text: localAnswer, spoken_text: localAnswer, model: data.model }]);
      emitSignal("navi.answer.received.v1", { language: prefs.language, model: data.model });
      // Auto-speak the reply (respects the global mute toggle)
      if (speech.supported && !speech.muted) {
        setTimeout(() => speech.speak(localAnswer, id), 40);
      }
    } catch (e) {
      const id = nextId();
      const fail = "I couldn't reach the AI service just now. Please try again in a moment.";
      const local = await translateIfNeeded(fail, prefs.language);
      setMessages(m => [...m, { id, role: "navi", text: local, spoken_text: local, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const toggleSpeak = useCallback((m) => {
    if (!speech.supported) return;
    if (speech.speakingKey === m.id) { speech.stop(); return; }
    speech.speak(m.spoken_text || m.text, m.id, { force: true });
  }, [speech]);

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="navi-bubble-open"
          className="fixed z-30 rounded-full shadow-lg flex items-center gap-2 pl-1.5 pr-4 py-1.5 text-white font-medium text-sm hover:scale-105 transition"
          style={{ background: NAVY, bottom: 96, right: 20 }}
        >
          <NaviFace size="sm" pulse />
          Ask NAVI
        </button>
      )}

      {open && (
        <div
          data-testid="navi-bubble-panel"
          className="fixed z-40 w-[calc(100vw-24px)] max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ bottom: 96, right: 12, maxHeight: "70vh" }}
        >
          <header className="flex items-center justify-between px-4 py-3" style={{ background: NAVY, color: "white" }}>
            <div className="flex items-center gap-2.5">
              <NaviFace size="md" />
              <div>
                <div className="text-sm font-semibold">NAVI</div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>
                  Explains · never advises
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {speech.supported && (
                <button
                  onClick={speech.toggleMute}
                  data-testid="navi-bubble-mute"
                  title={speech.muted ? "Turn NAVI's voice on" : "Silence NAVI's voice"}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/85 hover:text-white hover:bg-white/10 transition"
                  aria-label={speech.muted ? "Unmute NAVI" : "Mute NAVI"}
                >
                  {speech.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              )}
              <button
                onClick={() => { speech.stop(); setOpen(false); }}
                data-testid="navi-bubble-close"
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/85 hover:text-white hover:bg-white/10 transition"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" style={{ background: "#F8F9FA" }}>
            {messages.map((m) => {
              const isNavi = m.role !== "user";
              const speaking = speech.speakingKey === m.id;
              return (
                <div key={m.id} className={`flex items-end gap-2 ${isNavi ? "justify-start" : "justify-end"}`}>
                  {isNavi && <NaviFace size="xs" ring={false} pulse={speaking} />}
                  <div
                    data-testid={`navi-msg-${m.role}`}
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed flex items-start gap-2 ${
                      isNavi
                        ? m.error
                          ? "bg-rose-50 text-rose-800"
                          : "bg-white text-slate-800 border border-slate-100"
                        : "text-white"
                    }`}
                    style={!isNavi ? { background: NAVY } : {}}
                  >
                    <span className="flex-1 min-w-0">{m.text}</span>
                    {isNavi && speech.supported && (
                      <button
                        onClick={() => toggleSpeak(m)}
                        data-testid={`navi-msg-speak-${m.id}`}
                        className="flex-shrink-0 -mr-0.5 mt-0.5 opacity-60 hover:opacity-100 transition"
                        title={speaking ? "Stop" : "Read aloud"}
                        aria-label={speaking ? "Stop reading" : "Read this aloud"}
                      >
                        {speaking ? <Square className="w-3 h-3" fill="currentColor" /> : <Volume2 className="w-3 h-3" />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-slate-500 px-1">
                <NaviFace size="xs" ring={false} pulse />
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> NAVI is thinking…
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="flex items-center gap-2 p-2 border-t border-slate-200 bg-white">
            <input
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && ask()}
              placeholder="Ask about anything financial…"
              disabled={busy}
              data-testid="navi-bubble-input"
              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-slate-400"
            />
            <button
              onClick={ask}
              disabled={busy || !text.trim()}
              data-testid="navi-bubble-send"
              className="rounded-xl w-9 h-9 flex items-center justify-center text-white disabled:opacity-40"
              style={{ background: NAVY }}
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
